import {
  convertToTutor,
  convertToTutorConfig,
  Tutor,
  TutorAuthority,
  tutorCollectionKey,
  TutorConfig,
  tutorConfigCollectionKey
} from "@/entities/tutor";
import firebase from "firebase/app";
import "firebase/firestore";
import { getCurrentUnixtime } from "@/utils/date";
import { convertToRole, roleCollectionKey } from "@/entities/role";

interface TutorDetailWithClassrooms {
  id: string;
  name: string;
  authority: TutorAuthority;
  classroomNames: string[];
  gmail: string;
  microsoftEmail: string;
  email: string;
  passwordLoginDisabled?: boolean;
}

interface TutorDetailsOfSchool {
  schoolId: string;
  schoolName: string;
  isSchool?: boolean;
  tutorDetailsWithClassrooms: TutorDetailWithClassrooms[];
}

export type TutorMeta = {
  main: Tutor;
  config: TutorConfig;
};
export async function getTutor(
  ref: firebase.firestore.DocumentReference
): Promise<Tutor | null> {
  const snapshot = await ref.get();
  const data = snapshot.data();
  if (!snapshot.exists || !data) {
    return null;
  }
  if (!data.selectedPrompt) {
    const db = firebase.firestore();
    const promptDoc = await db
      .collection("prompt")
      .doc("promptForTutors")
      .get();
    data.selectedPrompt = [
      {
        name: promptDoc.data()!.name,
        text: promptDoc.data()!.text,
        docId: promptDoc.id
      }
    ];
  }
  return convertToTutor(data, snapshot.ref);
}

export async function fetchTutorConfig(
  ref: firebase.firestore.DocumentReference
): Promise<TutorConfig | null> {
  const snapshot = await ref.get();
  const data = snapshot.data();
  if (!snapshot.exists || !data) {
    return null;
  }

  return convertToTutorConfig(data, snapshot.ref);
}

export async function fetchTutorPrompts(
  ref: firebase.firestore.DocumentReference
): Promise<
  | {
      ref: firebase.firestore.DocumentReference<firebase.firestore.DocumentData>;
      data: { name: string; text: string };
    }[]
  | null
> {
  const snapshot = await ref.collection("prompts").get();
  if (snapshot.docs.length === 0) {
    const tutorSnapshot = await ref.get();
    const data = tutorSnapshot.data();
    if (!tutorSnapshot.exists || !data) {
      return null;
    }
    let prompt = "";
    if (!data.prompt) {
      const db = firebase.firestore();
      const promptDoc = await db
        .collection("prompt")
        .doc("promptForTutors")
        .get();
      prompt = promptDoc.data()!.text;
    } else {
      prompt = data.prompt;
    }
    const newDoc = ref.collection("prompts").doc().id;
    await ref.collection("prompts").doc(newDoc).set({
      name: "デフォルトプロンプト",
      text: prompt,
      updatedAt: getCurrentUnixtime()
    });
    await ref.update({
      selectedPrompt: {
        name: "デフォルトプロンプト",
        text: prompt,
        docId: newDoc
      }
    });
  }
  const snapshot2 = await ref.collection("prompts").get();
  return snapshot2.docs.map(doc => {
    const ref = doc.ref;
    const data = doc.data() as { name: string; text: string };
    return { ref, data };
  });
}

export async function fetchTutorsOfSchool(
  schoolRef: firebase.firestore.DocumentReference
): Promise<Tutor[]> {
  const snapshot = await schoolRef.collection(tutorCollectionKey).get();
  if (snapshot.empty) {
    return [];
  }
  return snapshot.docs.map(doc => convertToTutor(doc.data(), doc.ref));
}

export async function fetchTutorConfigsOfSchool(
  schoolRef: firebase.firestore.DocumentReference
): Promise<TutorConfig[]> {
  const snapshot = await schoolRef.collection(tutorConfigCollectionKey).get();
  if (snapshot.empty) {
    return [];
  }
  return snapshot.docs.map(doc => convertToTutorConfig(doc.data(), doc.ref));
}

export async function updateTutorName(
  ref: firebase.firestore.DocumentReference,
  name: string
): Promise<void> {
  await ref.update({ name });
}

export async function updateTutorPrompt(
  ref: firebase.firestore.DocumentReference,
  prompt: { name: string; text: string }
): Promise<void> {
  await ref.collection("prompts").add(prompt);
}

export async function updateTutorAllowEmailNotify(
  ref: firebase.firestore.DocumentReference,
  allowEmailNotify: boolean,
  allowBadgeEmailNotify: boolean
) {
  await ref.update({ allowEmailNotify, allowBadgeEmailNotify });
}

export async function deleteTutor(ref: firebase.firestore.DocumentReference) {
  await ref.delete();
}

export async function registerTutor(
  name: string,
  email: string,
  password: string,
  authority: TutorAuthority,
  classroomIds: string[],
  googleAccountEmail?: string,
  microSoftAccountEmail?: string,
  passwordLoginDisabled?: boolean
) {
  const registerTutor = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("register_tutor");

  const res = await registerTutor({
    name,
    email,
    password,
    authority,
    classrooms: classroomIds,
    googleAccountEmail,
    microSoftAccountEmail,
    passwordLoginDisabled
  });
  if (!res.data.success) {
    throw new Error("予期せぬエラーが発生しました");
  }
}

export async function updateTutor(
  tutorId: string,
  data: {
    name?: string;
    /** ログイン用メールアドレス。変えるときだけ送る（オーナーと一括更新を許可された管理者のみ） */
    email?: string;
    authority?: TutorAuthority;
    classrooms?: string[];
    googleAccountEmail?: string;
    microSoftAccountEmail?: string;
    allowBulkUpdateTutors?: boolean;
    passwordLoginDisabled?: boolean;
  }
) {
  const updateTutor = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("update_tutor");

  const res = await updateTutor({
    tutor: tutorId,
    data
  });
  if (!res.data.success) {
    throw new Error("予期せぬエラーが発生しました");
  }
}

export async function tutorUpdatePassword(
  ref: firebase.firestore.DocumentReference,
  password: string
) {
  const call = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("tutor_update_initial_password");
  const res = await call({ tutorId: ref.id, password });
  if (!res.data.success || !res.data.token)
    throw new Error("予期せぬエラーが発生しました");
  await firebase.auth().signInWithCustomToken(res.data.token);
}

export async function getExternalServiceEmailOfTutor(
  provider: "google" | "microsoft",
  tutorRef: firebase.firestore.DocumentReference
): Promise<string | null> {
  const snapshot = await firebase
    .firestore()
    .collection(roleCollectionKey)
    .doc(tutorRef.id)
    .get();
  if (!snapshot.exists || !snapshot.data()) return null;
  const role = convertToRole(
    snapshot.data() as firebase.firestore.DocumentData,
    snapshot.ref
  );
  return provider === "google"
    ? role.data.gmail ?? null
    : role.data.microsoftEmail ?? null;
}

export async function getTutorDetailsOfSchools(
  params: { schoolId: string } | { schoolIds: string[] }
): Promise<TutorDetailsOfSchool[]> {
  const callable = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("get_tutor_details_of_schools");
  const res = await callable(params);
  return res.data as TutorDetailsOfSchool[];
}

// 一括更新用の型定義
export interface BulkUpdateTutorData {
  id: string;
  name: string;
  authority: TutorAuthority;
  gmail: string;
  microsoftEmail: string;
  email: string;
  classroomNames: string[];
  passwordLoginDisabled?: boolean;
}

interface BulkUpdateTutorsResponse {
  updatedCount: number;
  skippedCount: number;
  errors: { id: string; name: string; message: string }[];
}

export async function bulkUpdateTutors(params: {
  schoolId: string;
  tutors: BulkUpdateTutorData[];
}): Promise<BulkUpdateTutorsResponse> {
  const callable = firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable("bulk_update_tutors");
  const res = await callable(params);
  return res.data as BulkUpdateTutorsResponse;
}
